import React, { useCallback } from 'react';
import { Columns3, ChevronDown } from 'lucide-react';
import type { DataSourceConfig } from '@/services/PagesServices/CustomReportBuilderService';
import { useReportBuilderState } from '../hooks/useReportBuilderState';

interface FieldsSectionProps {
  currentSource: DataSourceConfig | undefined;
}

const FieldsSection: React.FC<FieldsSectionProps> = ({ currentSource }) => {
  const expanded = useReportBuilderState(s => s.expandedSections.fields);
  const selectedFields = useReportBuilderState(s => s.selectedFields);
  const toggleSection = useReportBuilderState(s => s.toggleSection);
  const toggleField = useReportBuilderState(s => s.toggleField);
  const setSelectedFields = useReportBuilderState(s => s.setSelectedFields);

  const handleSelectAll = useCallback(() => {
    if (!currentSource) return;
    const allKeys = currentSource.fields.map(f => f.key);
    setSelectedFields(selectedFields.length === allKeys.length ? [] : allKeys);
  }, [currentSource, selectedFields.length, setSelectedFields]);

  return (
    <div className="crb-config-section">
      <div className="crb-config-header" onClick={() => toggleSection('fields')}>
        <div className="crb-config-header-left">
          <Columns3 size={18} />
          <span className="crb-config-header-title">Fields</span>
          {selectedFields.length > 0 && (
            <span className="crb-config-header-badge">{selectedFields.length}</span>
          )}
        </div>
        <ChevronDown size={16} className={`crb-config-chevron ${expanded ? 'open' : ''}`} />
      </div>

      {expanded && (
        <div className="crb-config-body">
          {currentSource ? (
            <>
              <div className="crb-select-all">
                <button className="crb-select-all-btn" onClick={handleSelectAll}>
                  {selectedFields.length === currentSource.fields.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="crb-field-count">
                  {selectedFields.length}/{currentSource.fields.length}
                </span>
              </div>
              <div className="crb-fields-list">
                {currentSource.fields.map(field => (
                  <div
                    key={field.key}
                    className="crb-field-item"
                    onClick={() => toggleField(field.key)}
                  >
                    <div className={`crb-field-checkbox ${selectedFields.includes(field.key) ? 'checked' : ''}`}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <span>{field.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ fontSize: '13px', color: 'var(--color-secondary-text)', margin: 0 }}>
              Select a data source first
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(FieldsSection);
